import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import {
  VStack,
  Box,
  Heading,
  Select,
  Input,
  Button,
  HStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Textarea,
  Text,
  useColorModeValue,
  Flex,
  ModalFooter,
  IconButton,
  FormHelperText,
  FormLabel,
  FormControl,
  Tooltip,
  Icon,
  Container,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import { FiBriefcase, FiMic, FiMove, FiVolumeX, FiShare2 } from "react-icons/fi";
import { hitBeApi } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import { VoiceAssistantMicButton } from "../../common/VoiceAssistantMicButton";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
} from "../../../utils/browserSpeechRecognition";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import { useNavigate } from "react-router-dom";
import { appStore } from "../../../store/Store";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import {
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  TimeIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { MdPause, MdDelete } from "react-icons/md"; // Example imports for pause and delete icons
import { formatDateWithOrdinal } from "../../../utils/utils";
import DeleteButtonWithConfirmation from "../utils/DeleteConfirmation";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";

/** Compare Mongo-style ids from API vs state without === type surprises. */
const taskIdEqual = (a, b) =>
  a != null && b != null && String(a) === String(b);

const Task = ({
  task,
  onClick,
  onStatusChange,
  onEditClick,
  onDeleteClick,
  handleDueDateChange,
  onDragTaskStart,
  onDragTaskOver,
  onDropOnTask,
  draggedTaskId,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const titleStripBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.100", "gray.600");
  const footerBorder = useColorModeValue("gray.100", "gray.600");
  const actionRailBg = useColorModeValue("gray.50", "gray.700");
  const actionRailBorder = useColorModeValue("gray.200", "gray.600");
  const titleColor = useColorModeValue("gray.800", "white");
  const titleHoverColor = useColorModeValue("teal.600", "teal.300");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const handleBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const handleHoverBg = useColorModeValue("teal.50", "whiteAlpha.200");
  const selectBg = useColorModeValue("white", "gray.700");
  const softBgNotDone = useColorModeValue("red.50", "rgba(254, 178, 178, 0.12)");
  const softBgInProgress = useColorModeValue("orange.50", "rgba(251, 211, 189, 0.12)");
  const softBgDone = useColorModeValue("green.50", "rgba(154, 230, 180, 0.12)");
  const softBgBlocked = useColorModeValue("orange.50", "rgba(251, 211, 189, 0.12)");
  const softBgDiscarded = useColorModeValue("gray.100", "whiteAlpha.100");

  const statusTheme = {
    "Not Done": {
      strip: "red.400",
      iconColor: "red.500",
      icon: <CloseIcon />,
      softBg: softBgNotDone,
    },
    "In Progress": {
      strip: "orange.300",
      iconColor: "orange.400",
      icon: <WarningIcon />,
      softBg: softBgInProgress,
    },
    Done: {
      strip: "green.400",
      iconColor: "green.500",
      icon: <CheckCircleIcon />,
      softBg: softBgDone,
    },
    Blocked: {
      strip: "orange.400",
      iconColor: "orange.500",
      icon: <MdPause />,
      softBg: softBgBlocked,
    },
    Discarded: {
      strip: "gray.400",
      iconColor: "gray.500",
      icon: <MdDelete />,
      softBg: softBgDiscarded,
    },
  };

  const [isEditing, setIsEditing] = useState(false); // State to toggle editing
  const [newTitle, setNewTitle] = useState(task.projectTitle);
  const [dueDate, setDueDate] = useState(
    task.dueDate ? new Date(task.dueDate) : null
  ); // Initialize as Date object or null
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setNewTitle(task.projectTitle);
    }
  }, [task.projectTitle, task._id, isEditing]);

  useEffect(() => {
    setDueDate(task.dueDate ? new Date(task.dueDate) : null);
  }, [task.dueDate, task._id]);

  const onDueDateChange = (date) => {
    setDueDate(date);
    handleDueDateChange(task._id, date); // Call the provided function with selected date
    setShowDatePicker(false); // Hide date picker after selecting a date
  };

  const handleEditToggle = () => {
    if (isEditing) {
      onEditClick(task._id, newTitle); // Save new title when done editing
    }
    setIsEditing(!isEditing); // Toggle between editing and viewing mode
  };

  const theme = statusTheme[task.status] || statusTheme["Not Done"];
  const dueLabel =
    dueDate instanceof Date && !isNaN(dueDate.getTime())
      ? dueDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  const titleScrollSx = {
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": { height: "6px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "full",
      bg: "blackAlpha.200",
    },
  };

  return (
    <Box
      role="group"
      position="relative"
      overflow="hidden"
      mb={3}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="4px"
      borderLeftColor={theme.strip}
      borderRadius="2xl"
      boxShadow="0 2px 8px rgba(0, 0, 0, 0.06)"
      transition="box-shadow 0.2s ease, transform 0.2s ease"
      _hover={{
        boxShadow: "0 10px 28px -12px rgba(0, 0, 0, 0.18)",
        transform: "translateY(-1px)",
      }}
      onDragOver={(e) => onDragTaskOver(e)}
      onDrop={(e) => onDropOnTask(e, task)}
      opacity={draggedTaskId === String(task._id) ? 0.5 : 1}
      sx={{
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          _hover: { transform: "none" },
        },
      }}
    >
      {/* Title: full width, one line + horizontal scroll */}
      <Box
        w="100%"
        minW={0}
        px={3}
        pt={3}
        pb={2}
        bg={titleStripBg}
        borderBottomWidth="1px"
        borderColor={footerBorder}
      >
        {isEditing ? (
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            size="sm"
            w="100%"
            onClick={(e) => e.stopPropagation()}
            variant="filled"
            bg={selectBg}
            borderRadius="lg"
            focusBorderColor="teal.400"
            placeholder="Task title…"
            fontWeight="semibold"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleEditToggle();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setNewTitle(task.projectTitle);
                setIsEditing(false);
              }
            }}
          />
        ) : (
          <Box
            w="100%"
            maxW="100%"
            overflowX="auto"
            overflowY="hidden"
            py={0.5}
            sx={titleScrollSx}
            onClick={(e) => {
              e.stopPropagation();
              onClick(task);
            }}
            cursor="pointer"
            title={task.projectTitle}
            aria-label={`Open task: ${task.projectTitle}`}
          >
            <Text
              as="span"
              display="inline-block"
              whiteSpace="nowrap"
              fontWeight="bold"
              fontSize="md"
              letterSpacing="-0.02em"
              lineHeight="1.4"
              color={titleColor}
              pr={1}
              _hover={{ color: titleHoverColor }}
            >
              {task.projectTitle}
            </Text>
          </Box>
        )}
      </Box>

      {/* Toolbar */}
      <Flex
        align="center"
        gap={3}
        flexWrap="wrap"
        w="100%"
        minW={0}
        px={3}
        py={3}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip label="Drag to reorder" placement="top" hasArrow openDelay={400}>
          <Flex
            align="center"
            justify="center"
            flexShrink={0}
            w="34px"
            h="34px"
            borderRadius="lg"
            bg={handleBg}
            borderWidth="1px"
            borderColor={footerBorder}
            cursor="grab"
            transition="background 0.15s ease, border-color 0.15s ease"
            _groupHover={{ bg: handleHoverBg, borderColor: "teal.200" }}
            _active={{ cursor: "grabbing" }}
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              onDragTaskStart(e, task);
            }}
            aria-label="Drag to reorder task"
          >
            <Icon as={FiMove} color={mutedColor} boxSize={4} />
          </Flex>
        </Tooltip>

        <Flex
          align="center"
          justify="center"
          flexShrink={0}
          w="34px"
          h="34px"
          borderRadius="lg"
          bg={theme.softBg}
          color={theme.iconColor}
          fontSize="md"
          borderWidth="1px"
          borderColor={footerBorder}
        >
          {theme.icon}
        </Flex>

        <Select
          flex="1 1 160px"
          minW={0}
          value={task.status}
          onChange={(e) => onStatusChange(task._id, e.target.value)}
          size="sm"
          fontWeight="semibold"
          borderRadius="lg"
          bg={selectBg}
          borderWidth="1px"
          borderColor={footerBorder}
          _hover={{ borderColor: "teal.300" }}
          _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px rgba(56, 178, 172, 0.45)" }}
        >
          <option value="Not Done">Not Done</option>
          <option value="In Progress">In Progress</option>
          <option value="Done">Done</option>
          <option value="Blocked">Blocked</option>
          <option value="Discarded">Discarded</option>
        </Select>

        {showDatePicker ? (
          <Input
            type="date"
            size="sm"
            flex="1 1 160px"
            minW={0}
            bg={selectBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={footerBorder}
            value={
              dueDate instanceof Date && !isNaN(dueDate.getTime())
                ? dueDate.toISOString().slice(0, 10)
                : ""
            }
            onChange={(e) => onDueDateChange(new Date(e.target.value))}
            onBlur={() => setShowDatePicker(false)}
            autoFocus
          />
        ) : (
          <Tooltip label={dueLabel ? `Due ${dueLabel}` : "Set due date"} hasArrow>
            <IconButton
              flexShrink={0}
              size="sm"
              variant="outline"
              colorScheme="purple"
              borderRadius="lg"
              h="34px"
              minW="34px"
              aria-label={dueLabel ? `Due date ${dueLabel}` : "Set due date"}
              icon={<Icon as={dueLabel ? TimeIcon : CalendarIcon} boxSize={4} />}
              onClick={() => setShowDatePicker(true)}
            />
          </Tooltip>
        )}

        <Flex
          align="center"
          flexShrink={0}
          gap={2}
          ml={{ base: 0, md: "auto" }}
          py={1.5}
          px={2.5}
          borderRadius="xl"
          bg={actionRailBg}
          borderWidth="1px"
          borderColor={actionRailBorder}
        >
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme={isEditing ? "green" : "teal"}
            icon={isEditing ? <CheckIcon boxSize={4} /> : <EditIcon boxSize={3.5} />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditToggle();
            }}
            aria-label={isEditing ? "Save title" : "Edit title"}
            borderRadius="lg"
          />
          <DeleteButtonWithConfirmation
            onDelete={onDeleteClick}
            itemId={task._id}
            size="sm"
            colorScheme="red"
            variant="ghost"
            ariaLabel="Delete task"
          />
        </Flex>
      </Flex>
    </Box>
  );
};

function chartXAxisIntervalByLength(len) {
  if (!len || len <= 0) return "preserveStartEnd";
  if (len <= 16) return 0;
  if (len <= 45) return 1;
  if (len <= 90) return 2;
  if (len <= 180) return 4;
  return 6;
}

function chartHeightForPoints(points, baseHeight, maxHeight = 820) {
  if (!points || points <= 0) return baseHeight;
  const extra = Math.min(maxHeight - baseHeight, Math.floor(points / 3));
  return Math.max(baseHeight, Math.min(maxHeight, baseHeight + extra));
}

function chartWidthForPoints(points, baseWidth) {
  if (!points || points <= 0) return baseWidth;
  const pxPerPoint =
    points > 900 ? 6 : points > 600 ? 7 : points > 360 ? 8 : points > 180 ? 10 : 12;
  return Math.max(baseWidth, points * pxPerPoint);
}

const TASK_TREND_SERIES_KEYS = ["newTasks", "completedTasks"];

/** Per row, zeros each listed key independently when strictly above maxVal (chart-only). */
function applyTaskTrendExcludeAbove(series, maxVal, keys = TASK_TREND_SERIES_KEYS) {
  const cap = Number(maxVal);
  if (!(cap > 0) || !Array.isArray(series) || !keys?.length) return series || [];
  return series.map((row) => {
    const next = { ...row };
    keys.forEach((k) => {
      const v = Number(next[k]) || 0;
      next[k] = v > cap ? 0 : v;
    });
    return next;
  });
}

/**
 * ResponsiveContainer often measures 0×0 inside Chakra modals. This observes the
 * host box and renders Recharts with explicit width/height in pixels.
 */
function ProjectModalChartMeasure({ active, children, fallback }) {
  const wrapRef = useRef(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    if (!active) {
      setDim({ w: 0, h: 0 });
      return undefined;
    }
    const el = wrapRef.current;
    if (!el) return undefined;

    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = Math.max(2, Math.floor(r.width));
      const h = Math.max(2, Math.floor(r.height));
      setDim((p) => (p.w !== w || p.h !== h ? { w, h } : p));
    };

    measure();
    let raf = 0;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    raf = requestAnimationFrame(measure);
    const tA = window.setTimeout(measure, 0);
    const tB = window.setTimeout(measure, 200);
    const tFallback = window.setTimeout(() => {
      const r = el.getBoundingClientRect();
      if (Math.floor(r.width) >= 50 && Math.floor(r.height) >= 50) return;
      if (typeof window === "undefined") return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setDim({
        w: Math.max(240, Math.floor(vw - 48)),
        h: Math.max(240, Math.floor(vh * 0.38)),
      });
    }, 400);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
      clearTimeout(tA);
      clearTimeout(tB);
      clearTimeout(tFallback);
    };
  }, [active]);

  return (
    <Box ref={wrapRef} w="100%" h="100%" minW={0} minH={0} position="relative">
      {!active || dim.w < 2 || dim.h < 2 ? (
        fallback ?? (
          <Flex w="full" h="full" align="center" justify="center">
            <Spinner color="teal.500" size="md" />
          </Flex>
        )
      ) : (
        children({ w: dim.w, h: dim.h })
      )}
    </Box>
  );
}

const ZONE_ACCENT = {
  "Not Done": "red.400",
  "In Progress": "orange.300",
  Done: "green.400",
  Blocked: "orange.400",
  Discarded: "gray.400",
};

const Zone = ({
  status,
  tasks,
  onTaskClick,
  onStatusChange,
  onEditTaskTitle,
  onDeleteTask,
  handleDueDateChange,
  onDragTaskStart,
  onDragTaskOver,
  onDropOnTask,
  onDropOnZone,
  draggedTaskId,
}) => {
  const shellBg = useColorModeValue("gray.50", "gray.900");
  const shellBorder = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(26, 32, 44, 0.92)");
  const headingColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const emptyBg = useColorModeValue("white", "gray.800");
  const accent = ZONE_ACCENT[status] || "teal.400";

  return (
    <Box
      minW={{ base: "100%", md: "280px", lg: "300px" }}
      flex={{ base: "1 1 100%", lg: "1 1 0" }}
      mx={{ base: 0, lg: 1 }}
      mt={{ base: 6, lg: 0 }}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={shellBorder}
      bg={shellBg}
      boxShadow="0 4px 24px -8px rgba(0, 0, 0, 0.12)"
      maxH={{ base: "72vh", lg: "calc(100vh - 200px)" }}
      minH={{ base: "280px", lg: "auto" }}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      onDragOver={(e) => onDragTaskOver(e)}
      onDrop={(e) => onDropOnZone(e, status)}
    >
      <Box
        px={4}
        py={3.5}
        bg={headerBg}
        backdropFilter="blur(10px)"
        borderBottomWidth="1px"
        borderColor={shellBorder}
        flexShrink={0}
      >
        <Flex align="center" justify="space-between" gap={3}>
          <HStack spacing={3} minW={0} align="center">
            <Box w="4px" h={9} borderRadius="full" bg={accent} flexShrink={0} />
            <Box minW={0}>
              <Heading
                size="sm"
                color={headingColor}
                fontWeight="bold"
                letterSpacing="-0.02em"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {status}
              </Heading>
              <Text fontSize="xs" color={mutedColor} mt={0.5}>
                Drop tasks here to change status
              </Text>
            </Box>
          </HStack>
          <Badge
            colorScheme="teal"
            borderRadius="full"
            px={2.5}
            py={0.5}
            fontSize="0.7rem"
            fontWeight="bold"
            variant="subtle"
          >
            {tasks?.length || 0}
          </Badge>
        </Flex>
      </Box>

      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={{ base: 2, md: 3 }}
        py={3}
        sx={{
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "full",
            bg: "blackAlpha.200",
          },
        }}
      >
        {tasks?.length > 0 ? (
          tasks.map((task) => (
            <Task
              key={task._id}
              task={task}
              onClick={onTaskClick}
              onStatusChange={onStatusChange}
              onEditClick={onEditTaskTitle}
              onDeleteClick={onDeleteTask}
              handleDueDateChange={handleDueDateChange}
              onDragTaskStart={onDragTaskStart}
              onDragTaskOver={onDragTaskOver}
              onDropOnTask={onDropOnTask}
              draggedTaskId={draggedTaskId}
            />
          ))
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={10}
            px={4}
            borderRadius="xl"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor={shellBorder}
            bg={emptyBg}
          >
            <Text fontSize="sm" fontWeight="medium" color={mutedColor} textAlign="center">
              Drop a task here
            </Text>
            <Text fontSize="xs" color={mutedColor} textAlign="center" mt={1} opacity={0.85}>
              Empty column — tasks you move in will land here.
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

const ProjectManager = () => {
  const [categories, setCategories] = useState([]);
  const [updatedCategoryName, setUpdatedCategoryName] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");

  const [selectedParentTaskId, setSelectedParentTaskId] = useState("");
  const [newTaskName, setNewTaskName] = useState("");
  const [newSubTaskName, setNewSubTaskName] = useState("");
  const [editSubTaskId, setEditSubTaskId] = useState("");
  const [selectedTask, setSelectedTask] = useState(null); // Track selected task
  const [dueDate, setDueDate] = useState("");
  const [isTaskBeingCreated, setIsTaskBeingCreated] = useState(false);
  const [isSubTaskBeingCreated, setIsSubTaskBeingCreated] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false); // Track modal state
  const [activeTab, setActiveTab] = useState("notes"); // "notes" or "subtasks"
  const [newNote, setNewNote] = useState(""); // New note to add
  const [isEditing, setIsEditing] = useState(false); // Track if editing
  const [isEditingCategory, setIsEditingCategory] = useState(false); // Track if editing
  const [editIndex, setEditIndex] = useState(null); // Index of the note being edited

  const [tasks, setTasks] = useState({
    "Not Done": [],
    "In Progress": [],
    Done: [],
    Blocked: [],
    Discarded: [],
  });
  const [subTasks, setSubTasks] = useState({
    "Not Done": [],
    "In Progress": [],
    Done: [],
    Blocked: [],
    Discarded: [],
  });

  const [projectVoiceDraft, setProjectVoiceDraft] = useState("");
  const [projectVoiceReply, setProjectVoiceReply] = useState("");
  const [projectVoiceLoading, setProjectVoiceLoading] = useState(false);
  const [projectVoiceListening, setProjectVoiceListening] = useState(false);
  const [projectVoiceSpeaking, setProjectVoiceSpeaking] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const submitProjectVoiceAssistantRef = useRef(async () => {});
  /** Root board (tasks by category): ignore stale GETs without blocking sub-task fetches. */
  const projectsRootBoardFetchSeqRef = useRef(0);
  /** Sub-task board: separate sequence so parent/child fetches do not cancel each other. */
  const projectsSubtaskBoardFetchSeqRef = useRef(0);

  const [taskTrendPayload, setTaskTrendPayload] = useState(null);
  const [taskTrendLoading, setTaskTrendLoading] = useState(false);
  const [isProjectAnalyticsOpen, setIsProjectAnalyticsOpen] = useState(false);
  const [projectTaskTrendExcludeAboveInput, setProjectTaskTrendExcludeAboveInput] =
    useState("");
  const shareProjectModal = useDisclosure();
  const [shareProjectCategory, setShareProjectCategory] = useState(null);

  const bgColor = useColorModeValue("gray.50", "gray.800");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.100", "gray.700");
  const assistantHeaderBg = useColorModeValue("gray.50", "gray.900");
  const subtleLabel = useColorModeValue("gray.600", "gray.400");
  const chartAxisColor = useColorModeValue("#64748b", "#94a3b8");
  const chartGridColor = useColorModeValue("#e2e8f033", "#ffffff14");
  const inputBgColor = useColorModeValue("teal.50", "teal.600");
  const focusBorderColor = useColorModeValue("teal.500", "teal.300");
  const buttonHoverBgColor = useColorModeValue("teal.400", "teal.500");
  const borderColor = useColorModeValue("teal.300", "teal.500");
  const hoverBgColor = useColorModeValue("teal.400", "teal.600");
  const hoverBorderColor = useColorModeValue("teal.400", "teal.600");
  const placeholderColor = useColorModeValue("gray.500", "gray.400"); // Color for placeholder text
  const textColor = useColorModeValue("gray.800", "white"); // Set text color based on color mode
  const modalBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("teal.100", "teal.600");
  const headerTextColor = useColorModeValue("teal.700", "teal.200");
  const noteTextColor = useColorModeValue("teal.600", "teal.300");
  const subTaskTextColor = useColorModeValue("blue.600", "blue.300");
  const buttonHoverColor = useColorModeValue("gray.800", "gray.300");
  const textPlaceholderColor = useColorModeValue("gray.400", "gray.500");

  const taskTrendChartHeight = useMemo(
    () =>
      chartHeightForPoints(taskTrendPayload?.series?.length || 0, 380, 1000),
    [taskTrendPayload?.series?.length]
  );

  const selectedCategoryRow = useMemo(
    () => categories.find((c) => String(c._id) === String(selectedCategoryId)),
    [categories, selectedCategoryId]
  );

  const toast = useToast();
  const navigate = useNavigate();

  const getAllTasks = (tasksObj) => {
    return Object.values(tasksObj).flat();
  };

  const handleCategoryChange = (e) => {
    setSelectedParentTaskId("");
    const selectedId = e.target.value;
    const selectedCat = categories.find((cat) => cat._id === selectedId);
    setSelectedCategoryId(selectedId);
    setUpdatedCategoryName(selectedCat?.name || "");
    setActiveTab("notes");
  };

  const handleParentTaskChange = (e) => {
    const selectedId = e.target.value;
    setActiveTab("notes");
    setSelectedParentTaskId(selectedId);
  };

  const handleEditNote = (index, currentNewNoteTitle) => {
    setIsEditing(true);
    setEditIndex(index);
    setNewNote(currentNewNoteTitle); // Pre-fill the textarea with the current note title
  };

  const handleEditSubTask = (index, currentNewSubTaskTitle, subTaskId) => {
    setIsEditing(true);
    setEditIndex(index);
    setEditSubTaskId(subTaskId);
    setNewSubTaskName(currentNewSubTaskTitle); // Pre-fill the textarea with the current note title
  };

  const handleDeleteNote = (index) => {
    const updatedNotes = selectedTask.notes.filter((_, i) => i !== index); // Remove the note

    // API call to update the task with the new notes array
    hitBeApi(`projects/${selectedTask._id}`, "PUT", {
      notes: updatedNotes,
    })
      .then((updatedTaskResponse) => {
        // Update state with new notes
        if (updatedTaskResponse.success) {
          setSelectedTask({
            ...selectedTask,
            notes: updatedTaskResponse.data.notes,
          });

          toast({
            title: "Note deleted.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        toast({
          title: "Error deleting note.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  const handleUpdateNote = async () => {
    if (!newNote || newNote?.trim() === "") {
      toast({
        title: "Note content is required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const updatedNotes = selectedTask.notes.map((note, index) => {
      if (index === editIndex) {
        return { title: newNote, addedAt: note.addedAt }; // Keep the original addedAt
      }
      return note;
    });

    try {
      // API call to update the task with the new notes array
      const updatedTaskResponse = await hitBeApi(
        `projects/${selectedTask._id}`,
        "PUT",
        {
          notes: updatedNotes, // Send updated notes to backend
        }
      );

      // Update the task in the state
      setSelectedTask({
        ...selectedTask,
        notes: updatedTaskResponse.data.notes,
      });

      setNewNote(""); // Clear the note input
      setIsEditing(false); // Reset editing state
      setEditIndex(null); // Reset edit index
      toast({
        title: "Note updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating note.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch categories of type project
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryResponse = await hitBeApi(
          "categories?type=project",
          "GET"
        );
        setCategories(categoryResponse.data);
      } catch (error) {
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        toast({
          title: "Error fetching categories.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const run = async () => {
      const fetchId = ++projectsRootBoardFetchSeqRef.current;
      const stale = () => fetchId !== projectsRootBoardFetchSeqRef.current;
      if (selectedCategoryId === "" || !selectedCategoryId) {
        if (!stale()) setTasks([]);
        return;
      }
      try {
        const projectsResponse = await hitBeApi(
          `projects?categoryId=${selectedCategoryId}`,
          "GET"
        );
        if (stale()) return;
        const projectTasks = projectsResponse.data;
        const organizedTasks = organizeTasksByStatus(projectTasks);
        setTasks(organizedTasks);
      } catch (error) {
        if (stale()) return;
        toast({
          title: "Error fetching tasks.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId]);

  useEffect(() => {
    if (!selectedCategoryId) {
      setTaskTrendPayload(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setTaskTrendLoading(true);
      try {
        const res = await hitBeApi(
          `projects/analytics/task-trends?categoryId=${encodeURIComponent(
            selectedCategoryId
          )}&days=45&_cb=${Date.now()}`,
          "GET"
        );
        if (cancelled) return;
        if (res?.success) setTaskTrendPayload(res.data);
        else setTaskTrendPayload(null);
      } catch {
        if (!cancelled) setTaskTrendPayload(null);
      } finally {
        if (!cancelled) setTaskTrendLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId]);

  const projectTaskTrendExcludeAboveParsed = useMemo(() => {
    const n = Number(
      String(projectTaskTrendExcludeAboveInput ?? "").replace(/,/g, "").trim()
    );
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [projectTaskTrendExcludeAboveInput]);

  const taskTrendSeriesForChart = useMemo(() => {
    const raw = taskTrendPayload?.series;
    if (!Array.isArray(raw)) return [];
    return applyTaskTrendExcludeAbove(raw, projectTaskTrendExcludeAboveParsed);
  }, [taskTrendPayload?.series, projectTaskTrendExcludeAboveParsed]);

  const taskTrendChartAllExcludedByThreshold = useMemo(() => {
    if (!(projectTaskTrendExcludeAboveParsed > 0) || !Array.isArray(taskTrendPayload?.series)) {
      return false;
    }
    const raw = taskTrendPayload.series;
    let rawMax = 0;
    raw.forEach((row) => {
      TASK_TREND_SERIES_KEYS.forEach((k) => {
        const v = Number(row[k]) || 0;
        if (v > rawMax) rawMax = v;
      });
    });
    if (!(rawMax > 0)) return false;
    let shownMax = 0;
    taskTrendSeriesForChart.forEach((row) => {
      TASK_TREND_SERIES_KEYS.forEach((k) => {
        const v = Number(row[k]) || 0;
        if (v > shownMax) shownMax = v;
      });
    });
    return !(shownMax > 0);
  }, [
    projectTaskTrendExcludeAboveParsed,
    taskTrendPayload?.series,
    taskTrendSeriesForChart,
  ]);

  const fetchProjects = async () => {
    if (selectedParentTaskId === "" || !selectedParentTaskId) {
      const fetchId = ++projectsRootBoardFetchSeqRef.current;
      const stale = () => fetchId !== projectsRootBoardFetchSeqRef.current;

      if (selectedCategoryId === "" || !selectedCategoryId) {
        if (!stale()) setTasks([]);
        return;
      }
      try {
        const projectsResponse = await hitBeApi(
          `projects?categoryId=${selectedCategoryId}`,
          "GET"
        );
        if (stale()) return;
        const projectTasks = projectsResponse.data;
        const organizedTasks = organizeTasksByStatus(projectTasks);
        setTasks(organizedTasks);
      } catch (error) {
        if (stale()) return;
        toast({
          title: "Error fetching tasks.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }
    const fetchId = ++projectsSubtaskBoardFetchSeqRef.current;
    const stale = () => fetchId !== projectsSubtaskBoardFetchSeqRef.current;
    try {
      const projectsResponse = await hitBeApi(
        `projects?parentId=${selectedParentTaskId}`,
        "GET"
      );
      if (stale()) return;
      const projectTasks = projectsResponse.data;
      const organizedTasks = organizeTasksByStatus(projectTasks);
      setSubTasks(organizedTasks);
    } catch (error) {
      if (stale()) return;
      toast({
        title: "Error fetching sub-tasks.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const refreshProjectDataAfterVoice = async () => {
    try {
      const categoryResponse = await hitBeApi("categories?type=project", "GET");
      if (categoryResponse.success && Array.isArray(categoryResponse.data)) {
        setCategories(categoryResponse.data);
      }
    } catch {
      /* ignore */
    }
    await fetchProjects();
  };

  const stopProjectAssistantSpeech = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setProjectVoiceSpeaking(false);
  }, []);

  const speakProjectAssistantReply = useCallback((replyText) => {
    if (typeof window === "undefined" || !replyText) return;
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      speakBrowserAssistantReply(replyText, {
        lang: "en-IN",
        flushDelayMs: 0,
        onBeforeSpeak: () => setProjectVoiceSpeaking(true),
        onEnd: () => setProjectVoiceSpeaking(false),
        onError: () => setProjectVoiceSpeaking(false),
      });
    } catch {
      setProjectVoiceSpeaking(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const runProjectVoiceCaptureOnce = () => {
    stopProjectAssistantSpeech();
    startBrowserVoiceDictation({
      lang: getPreferredSttLang(),
      onListeningStart: () => setProjectVoiceListening(true),
      onInterim: (t) => setProjectVoiceDraft(t),
      onListeningEnd: () => setProjectVoiceListening(false),
      onFinal: (t) => {
        setProjectVoiceDraft(t);
        submitProjectVoiceAssistantRef.current(t);
      },
      onError: () => {},
    });
  };

  const submitProjectVoiceAssistant = async (overrideText) => {
    const raw =
      overrideText !== undefined && overrideText !== null
        ? String(overrideText)
        : projectVoiceDraft;
    const text = raw.trim();
    if (!text) {
      toast({
        title: "Nothing to send",
        description: "Type or dictate a command.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    stopProjectAssistantSpeech();
    setProjectVoiceLoading(true);
    try {
      const selectedCat = categories.find(
        (c) => String(c._id) === String(selectedCategoryId)
      );
      const res = await hitBeApi("projects/voice-assistant", "POST", {
        text,
        selectedCategoryId: selectedCategoryId || null,
        selectedCategoryName: selectedCat?.name ?? null,
      });
      if (res.success && res.data) {
        if (res.data.userTokenDetails) {
          const currentUser = appStore.getState().user || {};
          appStore.dispatch({
            type: "SET_USER",
            value: { ...currentUser, userTokenDetails: res.data.userTokenDetails },
          });
        }
        const reply = res.data.reply || "";
        setProjectVoiceReply(reply);
        if (res.data.actions?.length) {
          const deletedCat = res.data.actions.find(
            (a) => a.type === "category_deleted"
          );
          if (
            deletedCat?.data?._id &&
            String(selectedCategoryId) === String(deletedCat.data._id)
          ) {
            setSelectedCategoryId("");
            setUpdatedCategoryName("");
          }
          await refreshProjectDataAfterVoice();
        }
        const hasMutation = res.data.actions?.length > 0;
        toast({
          title: hasMutation ? "Projects updated" : "Assistant",
          description: reply || "Done.",
          status: hasMutation ? "success" : "info",
          duration: 4500,
          isClosable: true,
        });
        if (reply) {
          speakProjectAssistantReply(reply);
        }
      }
    } catch (error) {
      toast({
        title: "Assistant error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setProjectVoiceLoading(false);
    }
  };

  submitProjectVoiceAssistantRef.current = submitProjectVoiceAssistant;

  const startProjectVoiceRecognition = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge, or type below.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (projectVoiceLoading || projectVoiceListening) return;
    runProjectVoiceCaptureOnce();
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParentTaskId]);

  const organizeTasksByStatus = (tasksArray) => {
    // Group tasks by their status
    const groupedTasks = tasksArray.reduce(
      (acc, task) => {
        const status = task.status || "Not Done";
  
        // Initialize the status group if it doesn't exist
        if (!acc[status]) {
          acc[status] = [];
        }
  
        acc[status].push(task); // Add task to the corresponding status group
        return acc;
      },
      {
        "Not Done": [],
        "In Progress": [],
        Done: [],
        Blocked: [],
        Discarded: [],
      }
    );
    
  
    // Process each status group to arrange tasks based on orderIndex
    for (const status in groupedTasks) {

      const tasks = groupedTasks[status];

      let maxSlot = Math.max(1, tasks.length);
      for (let i = 0; i < tasks.length; i++) {
        const oi = tasks[i]?.orderIndex;
        if (oi != null && !Number.isNaN(Number(oi))) {
          maxSlot = Math.max(maxSlot, Number(oi) + 1);
        }
      }

      let orderedTasks = new Array(maxSlot).fill(null); // Slots 0..maxSlot-1
  
      const undefinedOrderTasks = [];
      const duplicateIndexTasks = []
  
      // Place tasks with defined orderIndex
      tasks.forEach((task) => {
        if (task.orderIndex !== undefined && !isNaN(task.orderIndex)) {
          if (orderedTasks[task.orderIndex] === null) {
            orderedTasks[task.orderIndex] = task;
          } else {
            duplicateIndexTasks.push(task);
          }
        } else {
          undefinedOrderTasks.push(task); // Collect tasks without orderIndex
        }
      });
  
      // Fill empty positions with tasks without orderIndex
      let undefinedTaskIndex = 0;
      for (let i = 0; i < orderedTasks.length; i++) {
        if (orderedTasks[i] === null && undefinedTaskIndex < undefinedOrderTasks.length) {
          orderedTasks[i] = undefinedOrderTasks[undefinedTaskIndex];
          undefinedTaskIndex++;
        }
      }

      // Remove null entries from the orderedTasks array
      orderedTasks = orderedTasks.filter(task => task !== null);

      const finalTasks = [...orderedTasks, ...duplicateIndexTasks];
  
      // Replace groupedTasks[status] with the orderedTasks array
      groupedTasks[status] = finalTasks;
    }
  
    return groupedTasks;
  };
  

  const handleCreateCategory = async () => {
    if (!newCategoryName) {
      toast({
        title: "Category name is required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const createCategoryResponse = await hitBeApi(`categories`, "POST", {
        name: newCategoryName,
        type: "project",
      });
      setCategories([...categories, createCategoryResponse.data]);
      toast({
        title: "Category created.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setNewCategoryName("");
    } catch (error) {
      toast({
        title: "Error creating category.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Function to update the selected category
  const handleCategoryUpdate = async () => {
    if (!selectedCategoryId || updatedCategoryName.length === 0) {
      toast({
        title: "Error",
        description: "Category ID and updated name are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateCategoryResponse = await hitBeApi(
        `categories/${selectedCategoryId}`,
        "PUT",
        { name: updatedCategoryName }
      );

      if (updateCategoryResponse.success) {
        const updatedCategories = categories.map((category) =>
          category._id === selectedCategoryId._id
            ? { ...category, name: updatedCategoryName }
            : category
        );
        setCategories(updatedCategories);
        setIsEditingCategory(false);

        toast({
          title: "Success",
          description: "Category updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Function to delete the selected category
  const handleCategoryDelete = async () => {
    if (!selectedCategoryId) {
      toast({
        title: "Error",
        description: "No category selected for deletion",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteCategoryResponse = await hitBeApi(
        `categories/${selectedCategoryId}`,
        "DELETE"
      );

      if (deleteCategoryResponse.success) {
        const updatedCategories = categories.filter(
          (category) => category._id !== selectedCategoryId._id
        );
        setCategories(updatedCategories);
        setSelectedCategoryId(null);

        toast({
          title: "Success",
          description: "Category deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error deleting category",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateTask = async () => {
    if (isTaskBeingCreated) {
      return;
    }
    if (!newTaskName || !selectedCategoryId) {
      toast({
        title: "Task name and category are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setIsTaskBeingCreated(true);
      const createTaskResponse = await hitBeApi(`projects`, "POST", {
        projectTitle: newTaskName,
        categoryId: selectedCategoryId,
        status: "Not Done",
        dueDate: dueDate,
        orderIndex: tasks["Not Done"].length,
      });
      if (createTaskResponse.success) {
        setTasks((prevTasks) => ({
          ...prevTasks,
          "Not Done": [...prevTasks["Not Done"], createTaskResponse.data],
        }));

        toast({
          title: "Task created.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setNewTaskName("");
        setDueDate("");
        fetchProjects();
      }
      setIsTaskBeingCreated(false);
    } catch (error) {
      toast({
        title: "Error creating task.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsTaskBeingCreated(false);
    }
  };

  const handleCreateSubTask = async () => {
    if (isSubTaskBeingCreated) {
      return;
    }
    if (!newSubTaskName || !selectedCategoryId) {
      toast({
        title: "Sub-Task name and category are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setIsSubTaskBeingCreated(true);
      const createTaskResponse = await hitBeApi(`projects`, "POST", {
        projectTitle: newSubTaskName,
        categoryId: selectedCategoryId,
        status: "Not Done",
        parentId: selectedTask?._id,
        dueDate: dueDate,
        orderIndex: subTasks["Not Done"].length,
      });
      if (createTaskResponse.success) {
        setSubTasks((prevTasks) => ({
          ...prevTasks,
          "Not Done": [...prevTasks["Not Done"], createTaskResponse.data],
        }));
        setNewSubTaskName("");
        setDueDate("");
        fetchProjects();
        toast({
          title: "Sub-Task created.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      setIsSubTaskBeingCreated(false);
    } catch (error) {
      setIsSubTaskBeingCreated(false);
      toast({
        title: "Error creating sub-task.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    const tid = String(taskId);
    const priorLen = (tasks[newStatus] || []).length;
    const snapshot = tasks;

    const nextBoard = (() => {
      const next = {
        "Not Done": [...(tasks["Not Done"] || [])],
        "In Progress": [...(tasks["In Progress"] || [])],
        Done: [...(tasks.Done || [])],
        Blocked: [...(tasks.Blocked || [])],
        Discarded: [...(tasks.Discarded || [])],
      };
      let moved = null;
      for (const status of Object.keys(next)) {
        const idx = next[status].findIndex((t) => taskIdEqual(t._id, tid));
        if (idx >= 0) {
          moved = { ...next[status][idx], status: newStatus };
          next[status] = next[status].filter((_, i) => i !== idx);
          next[newStatus] = [...(next[newStatus] || []), moved];
          break;
        }
      }
      return { next, moved };
    })();

    if (!nextBoard.moved) return;
    setTasks(nextBoard.next);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${nextBoard.moved._id}`,
        "PUT",
        { status: newStatus, orderIndex: priorLen }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setTasks((prev) => {
        const merged = { ...prev };
        for (const k of Object.keys(merged)) {
          merged[k] = merged[k].map((t) =>
            taskIdEqual(t._id, tid) ? { ...t, ...serverRow } : t
          );
        }
        return merged;
      });
      await fetchProjects();
      toast({
        title: "Task updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setTasks(snapshot);
      toast({
        title: "Error updating task.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSubTaskStatusChange = async (taskId, newStatus) => {
    const tid = String(taskId);
    const priorLen = (subTasks[newStatus] || []).length;
    const snapshot = subTasks;

    const nextBoard = (() => {
      const next = {
        "Not Done": [...(subTasks["Not Done"] || [])],
        "In Progress": [...(subTasks["In Progress"] || [])],
        Done: [...(subTasks.Done || [])],
        Blocked: [...(subTasks.Blocked || [])],
        Discarded: [...(subTasks.Discarded || [])],
      };
      let moved = null;
      for (const status of Object.keys(next)) {
        const idx = next[status].findIndex((t) => taskIdEqual(t._id, tid));
        if (idx >= 0) {
          moved = { ...next[status][idx], status: newStatus };
          next[status] = next[status].filter((_, i) => i !== idx);
          next[newStatus] = [...(next[newStatus] || []), moved];
          break;
        }
      }
      return { next, moved };
    })();

    if (!nextBoard.moved) return;
    setSubTasks(nextBoard.next);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${nextBoard.moved._id}`,
        "PUT",
        { status: newStatus, orderIndex: priorLen }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setSubTasks((prev) => {
        const merged = { ...prev };
        for (const k of Object.keys(merged)) {
          merged[k] = merged[k].map((t) =>
            taskIdEqual(t._id, tid) ? { ...t, ...serverRow } : t
          );
        }
        return merged;
      });
      await fetchProjects();
      toast({
        title: "Sub-Task updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setSubTasks(snapshot);
      toast({
        title: "Error updating sub-task.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTaskTitleChange = async (taskId, newTitle) => {
    const tid = String(taskId);
    const snapshot = tasks;
    let editedTask;
    const updatedTasks = { ...tasks };

    Object.keys(updatedTasks).forEach((status) => {
      const taskIndex = updatedTasks[status].findIndex((task) =>
        taskIdEqual(task._id, tid)
      );
      if (taskIndex > -1) {
        editedTask = {
          ...updatedTasks[status][taskIndex],
          projectTitle: newTitle,
        };
        updatedTasks[status][taskIndex] = editedTask;
      }
    });

    if (!editedTask) return;
    setTasks(updatedTasks);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${editedTask._id}`,
        "PUT",
        { projectTitle: newTitle }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setTasks((prevTasks) => {
        const merged = { ...prevTasks };
        Object.keys(merged).forEach((status) => {
          merged[status] = merged[status].map((task) =>
            taskIdEqual(task._id, tid) ? { ...task, ...serverRow } : task
          );
        });
        return merged;
      });
      await fetchProjects();
      toast({
        title: "Task title updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setTasks(snapshot);
      toast({
        title: "Error updating task title.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTaskDueDateChange = async (taskId, dueDate) => {
    const tid = String(taskId);
    const snapshot = tasks;
    let editedTask;
    const updatedTasks = { ...tasks };
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dueDate < tomorrow) {
      toast({
        title: "Error, please retry",
        description: "Due date can not be earlier than tomorrow",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    Object.keys(updatedTasks).forEach((status) => {
      const taskIndex = updatedTasks[status].findIndex((task) =>
        taskIdEqual(task._id, tid)
      );
      if (taskIndex > -1) {
        editedTask = {
          ...updatedTasks[status][taskIndex],
          dueDate: dueDate,
        };
        updatedTasks[status][taskIndex] = editedTask;
      }
    });

    if (!editedTask) return;
    setTasks(updatedTasks);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${editedTask._id}`,
        "PUT",
        { dueDate: dueDate }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setTasks((prevTasks) => {
        const merged = { ...prevTasks };
        Object.keys(merged).forEach((status) => {
          merged[status] = merged[status].map((task) =>
            taskIdEqual(task._id, tid) ? { ...task, ...serverRow } : task
          );
        });
        return merged;
      });
      await fetchProjects();
      toast({
        title: "Task due date updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setTasks(snapshot);
      toast({
        title: "Error updating task due date.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };


  const handleSubTaskTitleChange = async (taskId, newTitle) => {
    const tid = String(taskId);
    const snapshot = subTasks;
    let editedTask;
    const updatedSubTasks = { ...subTasks };

    Object.keys(updatedSubTasks).forEach((status) => {
      const taskIndex = updatedSubTasks[status].findIndex((task) =>
        taskIdEqual(task._id, tid)
      );
      if (taskIndex > -1) {
        editedTask = {
          ...updatedSubTasks[status][taskIndex],
          projectTitle: newTitle,
        };
        updatedSubTasks[status][taskIndex] = editedTask;
      }
    });

    if (!editedTask) return;
    setSubTasks(updatedSubTasks);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${editedTask._id}`,
        "PUT",
        { projectTitle: newTitle }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setSubTasks((prevTasks) => {
        const merged = { ...prevTasks };
        Object.keys(merged).forEach((status) => {
          merged[status] = merged[status].map((task) =>
            taskIdEqual(task._id, tid) ? { ...task, ...serverRow } : task
          );
        });
        return merged;
      });
      await fetchProjects();
      setNewSubTaskName("");
      setIsEditing(false);
      setEditIndex(null);

      toast({
        title: "SubTask title updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setSubTasks(snapshot);
      toast({
        title: "Error updating sub-task title.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSubTaskDueDateChange = async (taskId, dueDate) => {
    const tid = String(taskId);
    const snapshot = subTasks;
    let editedTask;
    const updatedSubTasks = { ...subTasks };

    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dueDate < tomorrow) {
      toast({
        title: "Error",
        description: "Due date can not be earlier than tomorrow",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    Object.keys(updatedSubTasks).forEach((status) => {
      const taskIndex = updatedSubTasks[status].findIndex((task) =>
        taskIdEqual(task._id, tid)
      );
      if (taskIndex > -1) {
        editedTask = {
          ...updatedSubTasks[status][taskIndex],
          dueDate: dueDate,
        };
        updatedSubTasks[status][taskIndex] = editedTask;
      }
    });

    if (!editedTask) return;
    setSubTasks(updatedSubTasks);

    try {
      const updatedTaskResponse = await hitBeApi(
        `projects/${editedTask._id}`,
        "PUT",
        { dueDate: dueDate }
      );
      if (!updatedTaskResponse?.success) {
        throw new Error(updatedTaskResponse?.message || "Update failed");
      }
      const serverRow = updatedTaskResponse.data || {};
      setSubTasks((prevTasks) => {
        const merged = { ...prevTasks };
        Object.keys(merged).forEach((status) => {
          merged[status] = merged[status].map((task) =>
            taskIdEqual(task._id, tid) ? { ...task, ...serverRow } : task
          );
        });
        return merged;
      });
      await fetchProjects();
      setNewSubTaskName("");
      setIsEditing(false);
      setEditIndex(null);

      toast({
        title: "SubTask due date updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      setSubTasks(snapshot);
      toast({
        title: "Error updating sub-task due date.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const buildReorderedState = (board, source, destination) => {
    const next = {
      "Not Done": [...(board["Not Done"] || [])],
      "In Progress": [...(board["In Progress"] || [])],
      Done: [...(board.Done || [])],
      Blocked: [...(board.Blocked || [])],
      Discarded: [...(board.Discarded || [])],
    };
    const sourceList = [...(next[source.status] || [])];
    const destinationList =
      source.status === destination.status
        ? sourceList
        : [...(next[destination.status] || [])];

    const fromIndex = sourceList.findIndex((t) => String(t._id) === String(source.taskId));
    if (fromIndex < 0) return null;
    const [movedTask] = sourceList.splice(fromIndex, 1);
    const normalizedMovedTask = { ...movedTask, status: destination.status };

    let targetIndex = destination.index;
    if (targetIndex < 0 || targetIndex > destinationList.length) {
      targetIndex = destinationList.length;
    }
    if (source.status === destination.status && fromIndex < targetIndex) {
      targetIndex -= 1;
    }
    destinationList.splice(targetIndex, 0, normalizedMovedTask);

    next[source.status] = sourceList;
    next[destination.status] = destinationList;
    return next;
  };

  const buildBoardPositionMap = (board) => {
    const map = new Map();
    Object.entries(board).forEach(([status, statusTasks]) => {
      (statusTasks || []).forEach((task, index) => {
        map.set(String(task._id), { status, index });
      });
    });
    return map;
  };

  /** Only PUT tasks whose column or index actually changed (avoids N requests + rate limits). */
  const persistReorderedBoardDiff = async (beforeBoard, afterBoard) => {
    const beforePos = buildBoardPositionMap(beforeBoard);
    const afterPos = buildBoardPositionMap(afterBoard);
    const updates = [];
    for (const [id, afterMeta] of afterPos.entries()) {
      const prev = beforePos.get(id);
      if (!prev || prev.status !== afterMeta.status || prev.index !== afterMeta.index) {
        updates.push({
          id,
          payload: { status: afterMeta.status, orderIndex: afterMeta.index },
        });
      }
    }
    if (updates.length === 0) return;
    for (const u of updates) {
      await hitBeApi(`projects/${u.id}`, "PUT", u.payload);
    }
  };

  const getCurrentBoardAndSetter = () => {
    const isSubTaskBoard = !!(selectedParentTaskId && selectedParentTaskId.length > 0);
    return isSubTaskBoard
      ? { board: subTasks, setBoard: setSubTasks, typeLabel: "Sub-task" }
      : { board: tasks, setBoard: setTasks, typeLabel: "Task" };
  };

  const handleProjectDragStart = (e, task) => {
    setDraggedTask({
      taskId: String(task._id),
      sourceStatus: task.status || "Not Done",
    });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleProjectDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleProjectDrop = async (destinationStatus, destinationTask) => {
    if (!draggedTask?.taskId) return;
    const source = {
      taskId: draggedTask.taskId,
      status: draggedTask.sourceStatus,
    };
    const { board, setBoard, typeLabel } = getCurrentBoardAndSetter();
    const destinationList = board[destinationStatus] || [];
    const destinationIndex = destinationTask
      ? destinationList.findIndex((t) => String(t._id) === String(destinationTask._id))
      : destinationList.length;
    if (destinationIndex < 0) return;

    const before = board;
    const next = buildReorderedState(before, source, {
      status: destinationStatus,
      index: destinationIndex,
    });
    if (!next) return;

    setBoard(next);
    setDraggedTask(null);
    try {
      await persistReorderedBoardDiff(before, next);
      await fetchProjects();
      toast({
        title: `${typeLabel} moved.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      setBoard(before);
      toast({
        title: `Error moving ${typeLabel.toLowerCase()}.`,
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };


  const handleDeleteTask = async (taskId) => {
    const updatedTasks = { ...tasks };

    // Find and remove the task locally
    let deletedTask;
    Object.keys(updatedTasks).forEach((status) => {
      const taskIndex = updatedTasks[status].findIndex((task) =>
        taskIdEqual(task._id, taskId)
      );
      if (taskIndex > -1) {
        deletedTask = updatedTasks[status][taskIndex];
        updatedTasks[status].splice(taskIndex, 1); // Remove the task from the current status
      }
    });

    if (deletedTask) {
      setTasks(updatedTasks);

      try {
        await hitBeApi(`projects/${deletedTask._id}`, "DELETE");

        toast({
          title: "Task deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setNewTaskName(""); // Clear the note input
        setIsEditing(false); // Reset editing state
        setEditIndex(null); // Reset edit index
      } catch (error) {
        toast({
          title: "Error deleting task.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleDeleteSubTask = async (taskId) => {
    const updatedSubTasks = { ...subTasks };

    // Find and remove the task locally
    let deletedTask;
    Object.keys(updatedSubTasks).forEach((status) => {
      const taskIndex = updatedSubTasks[status].findIndex((task) =>
        taskIdEqual(task._id, taskId)
      );
      if (taskIndex > -1) {
        deletedTask = updatedSubTasks[status][taskIndex];
        updatedSubTasks[status].splice(taskIndex, 1); // Remove the task from the current status
      }
    });

    if (deletedTask) {
      setSubTasks(updatedSubTasks);

      try {
        await hitBeApi(`projects/${deletedTask._id}`, "DELETE");

        toast({
          title: "Sub-Task deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setNewSubTaskName(""); // Clear the note input
        setIsEditing(false); // Reset editing state
        setEditIndex(null); // Reset edit index
      } catch (error) {
        toast({
          title: "Error deleting sub-task.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleTaskClick = async (task) => {
    if (!selectedParentTaskId || selectedParentTaskId === "") {
      try {
        const projectsResponse = await hitBeApi(
          `projects?parentId=${task?._id}`,
          "GET"
        );
        const projectTasks = projectsResponse.data;
        const organizedTasks = organizeTasksByStatus(projectTasks);
        setSubTasks(organizedTasks);
      } catch (error) {
        toast({
          title: "Error fetching sub-tasks.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }

    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleAddNote = async () => {
    if (!newNote || newNote?.trim() === "") {
      toast({
        title: "Note content is required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newNoteObject = {
      title: newNote, // Set the title from the input
      addedAt: new Date(), // Set the current date as addedAt
    };

    const updatedNotes = [...(selectedTask.notes || []), newNoteObject]; // Add new note object to task's notes array

    try {
      // API call to update the task with the new notes array
      const updatedTaskResponse = await hitBeApi(
        `projects/${selectedTask._id}`,
        "PUT",
        {
          notes: updatedNotes, // Send updated notes to backend
        }
      );

      // Update the task in the state
      setTasks((prevTasks) => {
        const updatedTasks = { ...prevTasks };
        const statusTasks = updatedTasks[selectedTask.status].map((task) =>
          taskIdEqual(task._id, selectedTask._id)
            ? { ...task, notes: updatedTaskResponse.data.notes }
            : task
        );
        updatedTasks[selectedTask.status] = statusTasks;
        return updatedTasks;
      });

      setSelectedTask({
        ...selectedTask,
        notes: updatedTaskResponse.data.notes,
      });

      setNewNote(""); // Clear the note input
      toast({
        title: "Note added.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error adding note.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
    <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch" w="100%">
          <Card
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
          >
            <Box
              bgGradient="linear(to-r, cyan.600, teal.600)"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
            >
              <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                direction={{ base: "column", sm: "row" }}
              >
                <Flex align="flex-start" gap={4} direction={{ base: "column", sm: "row" }} flex="1" minW={0}>
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="xl"
                    bg="whiteAlpha.200"
                    flexShrink={0}
                  >
                    <Icon as={FiBriefcase} boxSize={7} color="white" />
                  </Flex>
                  <Box textAlign="left">
                    <Heading as="h1" size="lg" color="white" fontWeight="extrabold" letterSpacing="tight">
                      Projects
                    </Heading>
                    <Text fontSize="sm" color="whiteAlpha.900" mt={2} maxW="3xl" lineHeight="tall">
                      Categories, Kanban-style columns, due dates, notes, and voice commands —
                      aligned with the rest of Manage360.
                    </Text>
                  </Box>
                </Flex>
                <TabCacheButton tabId="project" onGradient />
              </Flex>
            </Box>
          </Card>

          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={borderColor}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardHeader
              pb={2}
              borderBottomWidth="1px"
              borderColor={mutedLine}
              bg={assistantHeaderBg}
            >
              <HStack spacing={3}>
                <Icon as={FiMic} color="teal.500" boxSize={5} />
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color={textColor}>
                    Voice &amp; text assistant
                  </Text>
                  <Text fontSize="xs" color={subtleLabel}>
                    Create categories and tasks, or update status and dates.
                  </Text>
                </Box>
              </HStack>
            </CardHeader>
            <CardBody px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} textAlign="left">
        <Text fontSize="xs" color={placeholderColor} mb={3}>
          Create categories, add or update tasks, set status or due dates, or delete.
          New tasks use the category selected in the dropdown below unless you name
          another folder in your message.
          Example: &quot;Create category Sprint 2&quot;, &quot;Add task wireframe
          mockups&quot;, &quot;Mark design review as In Progress&quot;, &quot;Delete
          task old backlog&quot;.
        </Text>
        <Textarea
          value={projectVoiceDraft}
          onChange={(e) => setProjectVoiceDraft(e.target.value)}
          placeholder='Example: "Add task API review under Work category"'
          size="sm"
          rows={3}
          bg={inputBgColor}
          borderColor={borderColor}
          _focus={{ borderColor: focusBorderColor, boxShadow: "outline" }}
          mb={3}
        />
        <HStack spacing={3} flexWrap="wrap" align="center">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={() => submitProjectVoiceAssistant()}
            isLoading={projectVoiceLoading}
            loadingText="Sending"
          >
            Send to assistant
          </Button>
          <VoiceAssistantMicButton
            colorScheme="teal"
            tooltipLabel="Browser speech (Chrome / Edge). Sends text to the server."
            onClick={startProjectVoiceRecognition}
            isLoading={projectVoiceListening}
            isDisabled={projectVoiceLoading}
          />
          {projectVoiceSpeaking ? (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              leftIcon={<Icon as={FiVolumeX} />}
              onClick={stopProjectAssistantSpeech}
            >
              Stop speaking
            </Button>
          ) : null}
          {projectVoiceListening ? (
            <Text fontSize="xs" color="teal.600">
              Listening…
            </Text>
          ) : null}
        </HStack>
        {(projectVoiceListening || projectVoiceSpeaking) && (
          <HStack spacing={2} mt={2}>
            {projectVoiceListening ? (
              <Badge colorScheme="teal" variant="subtle">
                Listening
              </Badge>
            ) : null}
            {projectVoiceSpeaking ? (
              <Badge colorScheme="purple" variant="subtle">
                Speaking
              </Badge>
            ) : null}
          </HStack>
        )}
        {projectVoiceReply ? (
          <Box
            mt={3}
            p={3}
            borderRadius="md"
            bg={modalBg}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1}>
              Assistant
            </Text>
            <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">
              {projectVoiceReply}
            </Text>
          </Box>
        ) : null}
            </CardBody>
          </Card>

      {/* Category and Task Creation Section */}
      <Card
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        bg={surfaceCard}
        boxShadow="sm"
        overflow="hidden"
      >
        <CardBody p={{ base: 4, md: 5 }}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        alignItems="center"
        gap={4}
        mb={0}
      >
        {/* Input for New Category */}
        <Input
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          placeholder="New Category Name"
          variant="outline"
          size="lg"
          _focus={{
            borderColor: focusBorderColor,
            boxShadow: `0 0 0 1px ${focusBorderColor}`,
          }}
          width={{ base: "100%", lg: "40%" }}
          bg={inputBgColor}
          boxShadow="sm"
        />

        {/* Create Category Button */}
        <Button
          onClick={handleCreateCategory}
          colorScheme="teal"
          size="lg"
          px={6}
          variant="solid"
          _hover={{ bg: buttonHoverBgColor, transform: "scale(1.05)" }}
          boxShadow="md"
          width={{ base: "100%", lg: "auto" }} // Full width on mobile, auto on larger screens
        >
          Create New Category
        </Button>
      </Flex>
        </CardBody>
      </Card>
      {/* Task Zones Section */}
      <Box width="100%" py={2}>
        <Flex
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
          bg={surfaceCard}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="md"
          p={{ base: 4, md: 6 }}
          gap={6}
        >
          {/* Instruction Text */}
          <Text
            fontSize={{ base: "md", lg: "lg" }}
            fontWeight="medium"
            color={textColor}
            textAlign={{ base: "center", lg: "left" }}
          >
            Select a category to view and manage related tasks.
          </Text>

          {/* Category Selector */}
          <Select
            placeholder="Select Category"
            onChange={handleCategoryChange}
            variant="outline"
            size="lg"
            borderColor={borderColor}
            _hover={{ borderColor: hoverBorderColor }}
            _focus={{
              borderColor: focusBorderColor,
              boxShadow: "0 0 0 2px teal.300",
            }}
            width={{ base: "100%", lg: "45%" }}
            bg={bgColor}
            color={textColor}
            _placeholder={{ color: placeholderColor }}
            borderRadius="md"
            shadow="sm"
          >
            {categories.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Flex>

        {/* Update and Delete Section */}
        {selectedCategoryId && (
          <Flex
            mt={8}
            p={{ base: 4, lg: 8 }}
            borderWidth={2}
            borderRadius="lg"
            align="center"
            justify="space-between"
            direction="column"
            bgGradient="linear(to-r, teal.400, green.300)"
            color="white"
            cursor="pointer"
            transition="all 0.3s ease"
            width={{ base: "100%", lg: "70%" }}
            mx="auto"
            _hover={{
              transform: "scale(1.05)",
              bgGradient: "linear(to-r, teal.300, green.200)",
              boxShadow: "2xl",
            }}
            boxShadow="lg"
          >
            <HStack spacing={4} width="100%" justify="center" align="center">
              {isEditingCategory ? (
                <Input
                  value={updatedCategoryName}
                  onChange={(e) => setUpdatedCategoryName(e.target.value)}
                  placeholder="Edit category name"
                  size="lg"
                  variant="flushed"
                  color="white"
                  onClick={(e) => e.stopPropagation()}
                  textAlign="center"
                  fontSize="xl"
                />
              ) : (
                <Text fontWeight="bold" fontSize="2xl" textAlign="center">
                  Selected Category: {updatedCategoryName}
                </Text>
              )}
            </HStack>

            <HStack spacing={4} mt={4}>
              {isEditingCategory ? (
                <IconButton
                  icon={<CheckIcon />}
                  size="lg"
                  colorScheme="teal"
                  variant="solid"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryUpdate();
                  }}
                  aria-label="Save Category Edit"
                />
              ) : (
                <IconButton
                  icon={<EditIcon />}
                  size="lg"
                  colorScheme="teal"
                  variant="solid"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditingCategory(true);
                  }}
                  aria-label="Edit Category"
                />
              )}

              {selectedCategoryRow &&
              (!selectedCategoryRow.collaborationShared ||
                selectedCategoryRow.collaborationCanInvite) ? (
                <Tooltip
                  label="Invite others — they see this category and its tasks."
                  hasArrow
                  openDelay={400}
                >
                  <IconButton
                    icon={<Icon as={FiShare2} />}
                    size="lg"
                    colorScheme="purple"
                    variant="solid"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareProjectCategory(selectedCategoryRow);
                      shareProjectModal.onOpen();
                    }}
                    aria-label="Share project category"
                  />
                </Tooltip>
              ) : null}

              <DeleteButtonWithConfirmation
                onDelete={() => handleCategoryDelete(selectedCategoryId)}
                itemId={selectedCategoryId}
                size="lg"
                colorScheme="red"
                ariaLabel="Delete Category"
              />
            </HStack>
          </Flex>
        )}

        {selectedCategoryId ? (
          <>
            <Box mt={10} textAlign="center">
              <Button
                colorScheme="teal"
                size="md"
                borderRadius="lg"
                onClick={() => setIsProjectAnalyticsOpen(true)}
              >
                Show analytics — task activity
              </Button>
              <Text fontSize="xs" color={subtleLabel} mt={2}>
                Full-screen chart with wide X-axis and auto height.
              </Text>
            </Box>
            <Modal
              isOpen={isProjectAnalyticsOpen}
              onClose={() => setIsProjectAnalyticsOpen(false)}
              size="2xl"
              motionPreset="scale"
              scrollBehavior="inside"
              closeOnOverlayClick={false}
              closeOnEsc
              blockScrollOnMount
              isCentered
            >
              <ModalOverlay bg="blackAlpha.500" />
              <ModalContent
                mx={{ base: 3, md: 6 }}
                borderRadius="2xl"
                display="flex"
                flexDirection="column"
                boxShadow="2xl"
                bg={surfaceCard}
                borderWidth="1px"
                borderColor={borderColor}
                maxW="min(92vw, 980px)"
              >
                <ModalHeader
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                  py={4}
                  px={{ base: 4, md: 6 }}
                  flexShrink={0}
                  bg={assistantHeaderBg}
                >
                  <Flex align="flex-start" justify="space-between" gap={4} flexWrap="wrap">
                    <Box>
                      <Heading size="md" color={headerTextColor}>
                        Task activity — new vs completed
                      </Heading>
                      <Text fontSize="sm" color={subtleLabel} mt={2} fontWeight="normal">
                        Last 45 UTC days • all tasks tied to this category.
                      </Text>
                      <HStack mt={3} spacing={2} flexWrap="wrap" align="flex-end">
                        <FormControl maxW={{ base: "100%", sm: "200px" }} w="full">
                          <FormLabel fontSize="xs" color={subtleLabel} mb={1}>
                            Hide chart values above (per day)
                          </FormLabel>
                          <Input
                            size="sm"
                            type="text"
                            inputMode="decimal"
                            placeholder="Off — full scale"
                            value={projectTaskTrendExcludeAboveInput}
                            onChange={(e) =>
                              setProjectTaskTrendExcludeAboveInput(e.target.value)
                            }
                            bg={inputBgColor}
                            aria-describedby="proj-trend-exclude-hint"
                          />
                        </FormControl>
                        <HStack spacing={1} flexWrap="wrap">
                          {[25000, 50000, 100000].map((preset) => (
                            <Button
                              key={preset}
                              size="xs"
                              variant={
                                projectTaskTrendExcludeAboveParsed === preset
                                  ? "solid"
                                  : "outline"
                              }
                              colorScheme="teal"
                              borderRadius="md"
                              onClick={() =>
                                setProjectTaskTrendExcludeAboveInput(String(preset))
                              }
                            >
                              {preset >= 100000 ? "1 lakh" : `${preset / 1000}k`}
                            </Button>
                          ))}
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setProjectTaskTrendExcludeAboveInput("")}
                          >
                            Clear
                          </Button>
                        </HStack>
                      </HStack>
                      <Text
                        id="proj-trend-exclude-hint"
                        fontSize="xs"
                        color={subtleLabel}
                        mt={1}
                      >
                        Each day: &quot;New&quot; and &quot;Completed&quot; counts above this
                        are omitted separately so quieter days stay visible.
                      </Text>
                      <HStack mt={3} spacing={3} align="center" flexWrap="wrap">
                        {taskTrendLoading ? (
                          <>
                            <Spinner size="sm" color="teal.400" />
                            <Text fontSize="xs" color={subtleLabel}>
                              Loading…
                            </Text>
                          </>
                        ) : null}
                        {projectTaskTrendExcludeAboveParsed > 0 ? (
                          <Badge colorScheme="cyan" borderRadius="md">
                            Hiding &gt;{" "}
                            {projectTaskTrendExcludeAboveParsed.toLocaleString("en-IN")}
                          </Badge>
                        ) : null}
                      </HStack>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="solid"
                      onClick={() => setIsProjectAnalyticsOpen(false)}
                    >
                      Close
                    </Button>
                  </Flex>
                </ModalHeader>
                <ModalBody
                  flex="1 1 auto"
                  minH={0}
                  minW={0}
                  display="flex"
                  flexDirection="column"
                  overflowY="auto"
                  px={{ base: 4, md: 6 }}
                  py={5}
                >
                  <Box
                    w="full"
                    position="relative"
                    minH={{
                      base: `${chartHeightForPoints(
                        taskTrendPayload?.series?.length || 0,
                        360
                      )}px`,
                      md: `${chartHeightForPoints(
                        taskTrendPayload?.series?.length || 0,
                        420
                      )}px`,
                    }}
                    overflow="hidden"
                  >
                    {taskTrendLoading && !(taskTrendPayload?.series?.length > 0) ? (
                      <Flex
                        align="center"
                        justify="center"
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                      >
                        <Spinner color="teal.500" size="lg" />
                      </Flex>
                    ) : taskTrendPayload?.series?.length ? (
                      <Box w="full">
                        <ProjectModalChartMeasure active={isProjectAnalyticsOpen}>
                          {({ w }) => (
                            <Box w="100%" overflowX="auto" overflowY="visible" py={3}>
                              <Box
                                w={chartWidthForPoints(
                                  taskTrendPayload?.series?.length || 0,
                                  w
                                )}
                                h={taskTrendChartHeight}
                              >
                                <LineChart
                                  width={chartWidthForPoints(
                                    taskTrendPayload?.series?.length || 0,
                                    w
                                  )}
                                  height={taskTrendChartHeight}
                                  data={taskTrendSeriesForChart}
                                  margin={{ top: 16, right: 24, left: 12, bottom: 24 }}
                                >
                                  <CartesianGrid
                                    strokeDasharray="5 10"
                                    stroke={chartGridColor}
                                  />
                                  <XAxis
                                    dataKey="day"
                                    tick={{ fontSize: 11, fill: chartAxisColor }}
                                    tickFormatter={(d) =>
                                      typeof d === "string" ? d.slice(5) : d
                                    }
                                    interval={chartXAxisIntervalByLength(
                                      taskTrendPayload?.series?.length || 0
                                    )}
                                    minTickGap={16}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12, fill: chartAxisColor }}
                                    allowDecimals={false}
                                  />
                                  <RechartsTooltip
                                    contentStyle={{ borderRadius: 12 }}
                                    formatter={(val) =>
                                      typeof val === "number"
                                        ? val.toLocaleString("en-IN")
                                        : val
                                    }
                                  />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="newTasks"
                                    name="New tasks"
                                    stroke="#0ea5e9"
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2 }}
                                    activeDot={{ r: 7 }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="completedTasks"
                                    name="Completed"
                                    stroke="#16a34a"
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2 }}
                                    activeDot={{ r: 7 }}
                                  />
                                </LineChart>
                              </Box>
                            </Box>
                          )}
                        </ProjectModalChartMeasure>
                        {taskTrendChartAllExcludedByThreshold ? (
                          <Text
                            fontSize="xs"
                            color="orange.500"
                            mt={2}
                            textAlign="center"
                          >
                            Every day in this range exceeds your threshold on at least
                            one series — lower the cutoff or clear it to see the full
                            chart again.
                          </Text>
                        ) : null}
                      </Box>
                    ) : (
                      <Flex
                        align="center"
                        justify="center"
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        px={4}
                      >
                        <Text fontSize="sm" color={subtleLabel} textAlign="center">
                          No task movement in range yet — create or complete tasks to
                          see the shape.
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </ModalBody>
                <ModalFooter
                  py={3}
                  borderTopWidth="1px"
                  borderColor={borderColor}
                  flexShrink={0}
                  bg={assistantHeaderBg}
                >
                  <Text fontSize="xs" color={subtleLabel}>
                    Tip: press Esc to close.
                  </Text>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        ) : null}
      </Box>
      <Flex justifyContent="center" my={6} px={4}>
        {selectedCategoryId && !selectedParentTaskId && (
          <Flex
            direction="column"
            alignItems="center"
            gap={6}
            p={6}
            bg={bgColor}
            borderRadius="lg"
            boxShadow="lg"
            width={{ base: "100%", md: "80%", lg: "60%" }}
            textAlign="center"
          >
            <Box mb={4}>
              <Text fontSize="xl" fontWeight="semibold" color={textColor}>
                Create a New Task for the Selected Category
              </Text>
              <Text fontSize="sm" color="gray.500">
                Enter a name for your new task, set a due date, and associate it
                with the category above.
              </Text>
            </Box>

            <Box width="100%" maxWidth={{ base: "100%", lg: "80%" }}>
              <FormLabel fontSize="lg" color="gray.600" mb="1">
                Name
              </FormLabel>
              <Input
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                placeholder="Enter Task Name"
                size="lg"
                bg={inputBgColor}
                variant="filled"
                focusBorderColor={borderColor}
                borderRadius="md"
                _placeholder={{ color: "gray.400" }}
                _focus={{
                  boxShadow: `0 0 0 2px ${borderColor}`,
                }}
                width="100%"
                boxShadow="sm"
                aria-label="New Task Name" // Accessibility improvement
              />
            </Box>

            <Box width="100%" maxWidth={{ base: "100%", lg: "80%" }}>
              <FormLabel fontSize="lg" color="gray.600" mb="1">
                Due Date
              </FormLabel>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                size="lg"
                bg={inputBgColor}
                variant="filled"
                focusBorderColor={borderColor}
                borderRadius="md"
                _focus={{
                  boxShadow: `0 0 0 2px ${borderColor}`,
                }}
                width="100%"
                boxShadow="sm"
                aria-label="Due Date" // Accessibility improvement
              />
            </Box>

            {/* Create Task Button */}
            <Button
              onClick={handleCreateTask}
              isDisabled={isTaskBeingCreated}
              colorScheme="teal"
              size="lg"
              px={8}
              py={6}
              fontWeight="bold"
              _hover={{
                bg: hoverBgColor,
                transform: "scale(1.05)",
                transition: "0.2s",
              }}
              width="100%"
              maxWidth={{ base: "100%", md: "60%", lg: "40%" }}
              boxShadow="md"
              borderRadius="full"
            >
              Create Task
            </Button>
          </Flex>
        )}
      </Flex>
      <Box my={8} px={4} maxWidth="600px" mx="auto">
        <Flex direction="column" alignItems="center" textAlign="center" mb={4}>
          <Text fontSize="2xl" fontWeight="bold" color={textColor} mb={1}>
            Find Sub-Tasks for a Given Task
          </Text>
          <Text fontSize="md" color={placeholderColor}>
            Select a task below to view and manage related subtasks.
          </Text>
        </Flex>

        <FormControl id="taskSelector">
          <FormLabel
            fontSize="lg"
            fontWeight="semibold"
            color={textColor}
            textAlign="center"
            mb={2}
          >
            Choose a Task
          </FormLabel>

          <Select
            placeholder="Select Task"
            onChange={handleParentTaskChange}
            variant="filled"
            size="lg"
            borderColor={borderColor}
            _hover={{ borderColor: hoverBorderColor }}
            _focus={{
              borderColor: focusBorderColor,
              boxShadow: "0 0 0 1px teal.300",
            }}
            width="100%"
            bg={bgColor}
            color={textColor}
            _placeholder={{ color: placeholderColor }}
            borderRadius="md"
            shadow="md"
          >
            {getAllTasks(tasks)?.map((task) => (
              <option key={task._id} value={task._id}>
                {task.projectTitle}
              </option>
            ))}
          </Select>

          <FormHelperText
            fontSize="sm"
            color={placeholderColor}
            mt={2}
            textAlign="center"
          >
            Selecting a task will display its subtasks below for easy
            management.
          </FormHelperText>
        </FormControl>
      </Box>
      {selectedCategoryId &&
        (selectedParentTaskId ? (
          <Flex
            spacing={4}
            justify="space-between"
            minW={"100%"}
            overflowX={{ base: "visible", lg: "scroll" }}
            flexDirection={{ base: "column", lg: "row" }} // Column on small screens, row on large
          >
            <Zone
              status="Not Done"
              tasks={subTasks["Not Done"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleSubTaskStatusChange}
              onEditTaskTitle={handleSubTaskTitleChange}
              onDeleteTask={handleDeleteSubTask}
              handleDueDateChange={handleSubTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Not Done", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="In Progress"
              tasks={subTasks["In Progress"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleSubTaskStatusChange}
              onEditTaskTitle={handleSubTaskTitleChange}
              onDeleteTask={handleDeleteSubTask}
              handleDueDateChange={handleSubTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("In Progress", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Done"
              tasks={subTasks["Done"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleSubTaskStatusChange}
              onEditTaskTitle={handleSubTaskTitleChange}
              onDeleteTask={handleDeleteSubTask}
              handleDueDateChange={handleSubTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Done", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Blocked"
              tasks={subTasks["Blocked"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleSubTaskStatusChange}
              onEditTaskTitle={handleSubTaskTitleChange}
              onDeleteTask={handleDeleteSubTask}
              handleDueDateChange={handleSubTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Blocked", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Discarded"
              tasks={subTasks["Discarded"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleSubTaskStatusChange}
              onEditTaskTitle={handleSubTaskTitleChange}
              onDeleteTask={handleDeleteSubTask}
              handleDueDateChange={handleSubTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Discarded", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
          </Flex>
        ) : (
          <Flex
            spacing={4}
            justify="space-between"
            minW={"100%"}
            overflowX={"scroll"}
            flexDirection={{ base: "column", lg: "row" }} // Column on small screens, row on large
          >
            <Zone
              status="Not Done"
              tasks={tasks["Not Done"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleTaskStatusChange}
              onEditTaskTitle={handleTaskTitleChange}
              onDeleteTask={handleDeleteTask}
              handleDueDateChange={handleTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Not Done", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="In Progress"
              tasks={tasks["In Progress"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleTaskStatusChange}
              onEditTaskTitle={handleTaskTitleChange}
              onDeleteTask={handleDeleteTask}
              handleDueDateChange={handleTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("In Progress", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Done"
              tasks={tasks["Done"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleTaskStatusChange}
              onEditTaskTitle={handleTaskTitleChange}
              onDeleteTask={handleDeleteTask}
              handleDueDateChange={handleTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Done", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Blocked"
              tasks={tasks["Blocked"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleTaskStatusChange}
              onEditTaskTitle={handleTaskTitleChange}
              onDeleteTask={handleDeleteTask}
              handleDueDateChange={handleTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Blocked", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
            <Zone
              status="Discarded"
              tasks={tasks["Discarded"]}
              onTaskClick={handleTaskClick}
              onStatusChange={handleTaskStatusChange}
              onEditTaskTitle={handleTaskTitleChange}
              onDeleteTask={handleDeleteTask}
              handleDueDateChange={handleTaskDueDateChange}
              onDragTaskStart={handleProjectDragStart}
              onDragTaskOver={handleProjectDragOver}
              onDropOnTask={(e, task) => {
                e.preventDefault();
                handleProjectDrop("Discarded", task);
              }}
              onDropOnZone={(e, status) => {
                e.preventDefault();
                handleProjectDrop(status, null);
              }}
              draggedTaskId={draggedTask?.taskId || null}
            />
          </Flex>
        ))}
        </VStack>
      </Container>
    </Box>
      {/* Modal for Task Details */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsEditing(false);
          setEditIndex(null);
          setNewNote("");
          setNewSubTaskName("");
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="2xl"
          boxShadow="2xl"
          bg={modalBg}
          maxW={{ base: "90vw", lg: "40vw" }}
        >
          <ModalHeader
            color={headerTextColor}
            fontSize="2xl"
            fontWeight="bold"
            textAlign="center"
            bg={headerBg}
            borderTopRadius="2xl"
            py={4}
          >
            Task Details
          </ModalHeader>
          <ModalCloseButton
            color="gray.600"
            _hover={{ color: buttonHoverColor }}
          />

          <HStack justifyContent="center" py={4} bg={headerBg} opacity="0.9">
            <Button
              onClick={() => setActiveTab("notes")}
              variant={activeTab === "notes" ? "solid" : "outline"}
              colorScheme="teal"
            >
              Notes
            </Button>
            <Button
              onClick={() => setActiveTab("subtasks")}
              display={
                selectedParentTaskId && selectedParentTaskId.length > 0
                  ? "none"
                  : "block"
              }
              variant={activeTab === "subtasks" ? "solid" : "outline"}
              colorScheme="blue"
            >
              Subtasks
            </Button>
          </HStack>

          <ModalBody p={6}>
            <VStack spacing={8} align="stretch">
              <HStack spacing={2}>
                <Text fontWeight="bold" fontSize="lg" color={noteTextColor}>
                  Task:
                </Text>
                <Text fontSize="md" color={textColor}>
                  {selectedTask?.projectTitle}
                </Text>
              </HStack>

              {activeTab === "notes" ? (
                <Box
                  flex="1"
                  border="1px solid"
                  borderRadius="lg"
                  borderColor={borderColor}
                  p={4}
                  bg={modalBg}
                  shadow="sm"
                >
                  <Text
                    fontWeight="bold"
                    color={noteTextColor}
                    fontSize="lg"
                    mb={2}
                  >
                    Notes
                  </Text>
                  <Box maxHeight="200px" overflowY="auto">
                    {selectedTask?.notes.map((note, index) => (
                      <Box
                        key={index}
                        p={2}
                        borderBottom="1px solid"
                        borderColor={borderColor}
                      >
                        <HStack justify="space-between">
                          <Box>
                            <Text fontWeight="bold" color={noteTextColor}>
                              {note.title}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Added at: {formatDateWithOrdinal(note.addedAt)}
                            </Text>
                          </Box>
                          <HStack spacing={2}>
                            <EditIcon
                              cursor="pointer"
                              color="teal.500"
                              _hover={{ color: "teal.700" }}
                              onClick={() => handleEditNote(index, note.title)}
                            />
                            <DeleteButtonWithConfirmation
                              onDelete={handleDeleteNote}
                              itemId={index}
                              size="sm"
                              colorScheme="red"
                              ariaLabel="Delete Note"
                            />
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                  <Textarea
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    variant="outline"
                    size="sm"
                    mt={3}
                    borderColor={borderColor}
                    borderRadius="md"
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.300",
                    }}
                    _placeholder={{ color: textPlaceholderColor }}
                  />
                </Box>
              ) : (
                <Box
                  flex="1"
                  border="1px solid"
                  borderRadius="lg"
                  borderColor={borderColor}
                  p={4}
                  bg={modalBg}
                  shadow="sm"
                >
                  <Text
                    fontWeight="bold"
                    color={subTaskTextColor}
                    fontSize="lg"
                    mb={2}
                  >
                    Subtasks
                  </Text>
                  <Box maxHeight="200px" overflowY="auto">
                    {getAllTasks(subTasks).map((subtask, index) => (
                      <Box
                        key={index}
                        p={2}
                        borderBottom="1px solid"
                        borderColor={borderColor}
                      >
                        <HStack justify="space-between">
                          <Box>
                            <Text
                              fontWeight="semibold"
                              color={subTaskTextColor}
                            >
                              {subtask.projectTitle}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              Status: {subtask.status}
                            </Text>
                          </Box>
                          <HStack spacing={2}>
                            <EditIcon
                              cursor="pointer"
                              color="blue.500"
                              _hover={{ color: "blue.700" }}
                              onClick={() =>
                                handleEditSubTask(
                                  index,
                                  subtask.projectTitle,
                                  subtask?._id
                                )
                              }
                            />
                            <DeleteButtonWithConfirmation
                              onDelete={() => handleDeleteSubTask(subtask?._id)}
                              itemId={index}
                              size="sm"
                              colorScheme="red"
                              ariaLabel="Delete Subtask"
                            />
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                  <Textarea
                    placeholder="Add a subtask..."
                    value={newSubTaskName}
                    onChange={(e) => setNewSubTaskName(e.target.value)}
                    variant="outline"
                    size="sm"
                    mt={3}
                    borderColor={borderColor}
                    borderRadius="md"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px blue.300",
                    }}
                    _placeholder={{ color: textPlaceholderColor }}
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            {activeTab === "notes" ? (
              <Button
                width={"100%"}
                onClick={isEditing ? handleUpdateNote : handleAddNote}
                colorScheme="teal"
                size="md"
                variant="solid"
                px={6}
                fontWeight="semibold"
                borderRadius="full"
                _hover={{
                  bg: "teal.400",
                  transform: "scale(1.05)",
                  boxShadow: "lg",
                }}
                _active={{ bg: "teal.500" }}
              >
                {isEditing ? "Update Note" : "Add Note"}
              </Button>
            ) : (
              <Button
                onClick={() =>
                  isEditing
                    ? handleSubTaskTitleChange(
                        editSubTaskId,
                        newSubTaskName,
                        true
                      )
                    : handleCreateSubTask()
                }
                isDisabled={isSubTaskBeingCreated}
                colorScheme="blue"
                size="md"
                variant="solid"
                px={6}
                fontWeight="semibold"
                width={"100%"}
                borderRadius="full"
                ml={3}
                _hover={{
                  bg: "blue.400",
                  transform: "scale(1.05)",
                  boxShadow: "lg",
                }}
                _active={{ bg: "blue.500" }}
                display={
                  selectedParentTaskId && selectedParentTaskId.length > 0
                    ? "none"
                    : "block"
                }
              >
                {isEditing ? "Update SubTask" : "Add SubTask"}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ShareCollaborationModal
        isOpen={shareProjectModal.isOpen}
        onClose={() => {
          shareProjectModal.onClose();
          setShareProjectCategory(null);
        }}
        titleLabel={
          shareProjectCategory
            ? `Share project category “${shareProjectCategory.name}”`
            : "Share project category"
        }
        kind="PROJECT_CATEGORY"
        anchor={{
          categoryAnchorId: shareProjectCategory?._id
            ? String(shareProjectCategory._id)
            : undefined,
        }}
      />
    </>
  );
};

export default ProjectManager;
